/**
 * Projects Component
 * Affiche les projets du portfolio sous forme de cards avec filtrage, modal de détails, et galerie d'images
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from "../../../../hooks";
import { apiService } from "../../../shared/services/apiService";
import { normalizeImageUrl } from "../../../shared/utils/imageUrl";
import { getProjectCategory as getSharedProjectCategory, type ProjectCategory } from "../../../shared/utils/projectCategory";
import { commonClasses } from "../../../../styles/indexStyles";
import * as projectsStyles from './projects.css';
import type { ProjectBackend as Project } from '../../../../types';

// Icônes
import {
  FaExternalLinkAlt,
  FaGithub,
  FaEye,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaRegTimesCircle,
  FaPlay,
  FaCode,
  FaStar,
  FaGraduationCap,
  FaBriefcase
} from 'react-icons/fa';

// Spinner
import { Spinner } from '../../../admin/components/Spinner/Spinner';

// Interfaces locales
interface ProjectImage {
  id: string;
  project_id: string;
  image_url: string;
  image_name: string;
  alt_text: string;
  is_primary: boolean;
  display_order: number;
  file_size: number;
  mime_type: string;
  created_at: string;
  updated_at: string;
}
interface ProjectFeature {
  id: string;
  project_id: string;
  name: string;
  description: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}
interface ExtendedProject extends Project {
  project_images?: ProjectImage[];
  project_features?: ProjectFeature[];
  images?: ProjectImage[];
  features?: ProjectFeature[];
}
interface ModalState {
  isOpen: boolean;
  project: ExtendedProject | null;
}
interface FilterState {
  active: string | null;
  visible: boolean;
}

// Filtres technos
const TECH_FILTERS = [
  'React', 'Node.js', 'TypeScript', 'JavaScript',
  'Python', 'PostgreSQL', 'MongoDB', 'Express',
  'Supabase', 'CSS3', 'HTML5', 'Git', 'Docker',
  'Next.js', 'Vue.js', 'Angular', 'GraphQL', 'Vite',
  'Vanilla Extract', 'Stripe', 'APIs', 'REST'
];

export const Projects: React.FC = () => {
  const { t, language } = useLanguage(); // Récupérer la langue courante
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  // État local
  const [projects, setProjects] = useState<ExtendedProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [modalState, setModalState] = useState<ModalState>({ isOpen: false, project: null });
  const [filterState, setFilterState] = useState<FilterState>({ active: null, visible: false });
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);
  const [showAcademic, setShowAcademic] = useState(false);

  // Effets
  useEffect(() => { if (inView) setIsVisible(true); }, [inView]);
  useEffect(() => { loadProjects(); }, []);
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalState.isOpen) closeModal();
    };
    if (modalState.isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [modalState.isOpen]);

  // Chargement des projets
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ PROTECTION ANTI-BOUCLE - Utilise apiService avec cache 3 minutes
      const response = await apiService.get<Project[]>('/projects', true, {
        cacheTtlMs: 180000, // 3 minutes cache
        useCache: true
      });
      
      if (response?.data) {
        const responseData = response.data as any;
        let projectsArray: any[] = [];
        if (Array.isArray(responseData)) {
          projectsArray = responseData;
        } else if (responseData && typeof responseData === 'object') {
          if (responseData.projects && Array.isArray(responseData.projects)) {
            projectsArray = responseData.projects;
          } else if (responseData.data && Array.isArray(responseData.data)) {
            projectsArray = responseData.data;
          } else if (responseData.items && Array.isArray(responseData.items)) {
            projectsArray = responseData.items;
          } else if (responseData.id) {
            projectsArray = [responseData];
          }
        }
        if (projectsArray.length > 0) {
          const mappedProjects: ExtendedProject[] = projectsArray.map((item: any) => ({
            id: String(item.id || ''),
            title: String(item.title || ''),
            title_en: item.title_en || null, // Ajout explicit des champs de traduction
            description: String(item.description || ''),
            description_en: item.description_en || null, // Ajout explicit des champs de traduction
            badge: item.badge || undefined,
            badge_en: item.badge_en || null, // Ajout explicit des champs de traduction
            technologies: Array.isArray(item.technologies) ? item.technologies : [],
            is_academic: Boolean(item.is_academic),
            live_url: item.live_url === null ? null : (item.live_url || null),
            github_url: item.github_url === null ? null : (item.github_url || null),
            image_url: item.image_url === null ? null : (item.image_url || null),
            badge_image_url: item.badge_image_url === null ? null : (item.badge_image_url || null),
            show_live_url: Boolean(item.show_live_url),
            show_github_url: Boolean(item.show_github_url),
            is_featured: Boolean(item.is_featured),
            display_order: Number(item.display_order) || 0,
            is_visible: Boolean(item.is_visible ?? true),
            created_at: String(item.created_at || new Date().toISOString()),
            updated_at: String(item.updated_at || new Date().toISOString()),
            project_images: Array.isArray(item.project_images) ? item.project_images :
              Array.isArray(item.images) ? item.images : [],
            images: Array.isArray(item.project_images) ? item.project_images :
              Array.isArray(item.images) ? item.images : [],
            project_features: Array.isArray(item.project_features) ? item.project_features :
              Array.isArray(item.features) ? item.features : [],
            features: Array.isArray(item.project_features) ? item.project_features :
              Array.isArray(item.features) ? item.features : []
          }));
          // Extraction du numéro du projet s'il commence par "Projet X"
          const extractProjectNumber = (title: string): number | null => {
            const match = title.match(/projet\s*(\d+)/i);
            return match ? parseInt(match[1], 10) : null;
          };

          const sortedProjects = mappedProjects.sort((a, b) => {
            const numA = extractProjectNumber(a.title);
            const numB = extractProjectNumber(b.title);

            // 1. Si les deux projets sont numérotés ("Projet 1", "Projet 2", ...),
            // on effectue un tri numérique naturel strict (Projet 1 avant Projet 2, etc.)
            if (numA !== null && numB !== null) {
              return numA - numB;
            }

            // 2. Pour les autres projets, les favoris sont placés en tête de liste
            const fa = a.is_featured ? 1 : 0;
            const fb = b.is_featured ? 1 : 0;
            if (fa !== fb && numA === null && numB === null) return fb - fa;

            // 3. Tri selon l'ordre personnalisé display_order s'il est défini
            if (a.display_order !== undefined && b.display_order !== undefined && a.display_order !== b.display_order) {
              return a.display_order - b.display_order;
            }
            if (a.display_order !== undefined && b.display_order === undefined) return -1;
            if (b.display_order !== undefined && a.display_order === undefined) return 1;

            // 4. Tri alphabétique naturel par titre (gestion des nombres et accents)
            return a.title.localeCompare(b.title, 'fr', { numeric: true, sensitivity: 'base' });
          });
          setProjects(sortedProjects);
          setRetryCount(0);
        } else {
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement des projets');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []); // Dependancies vides car on ne veut charger qu'une fois

  // Gestionnaires d'événements
  const openModal = (project: ExtendedProject) => {
    setModalState({ isOpen: true, project });
    setCurrentImageIndex(0);
    setImageLoading(false);
    document.body.style.overflow = 'hidden';
  };
  const closeModal = () => {
    setModalState({ isOpen: false, project: null });
    setCurrentImageIndex(0);
    setImageLoading(false);
    document.body.style.overflow = 'auto';
  };
  
  const handleFilter = (techFilter: string | null) => {
    setFilterState(_ => ({ active: techFilter, visible: false }));
  };
  const toggleFiltersVisibility = () => {
    setFilterState(prev => ({ ...prev, visible: !prev.visible }));
  };
  const clearFilters = () => {
    setFilterState({ active: null, visible: false });
  };
  const navigateImage = (direction: 'next' | 'prev') => {
    if (!modalState.project?.project_images?.length) return;
    setImageLoading(true);
    setTimeout(() => {
      if (direction === 'next') {
        setCurrentImageIndex(prev =>
          (prev + 1) % modalState.project!.project_images!.length
        );
      } else {
        setCurrentImageIndex(prev =>
          prev === 0 ? modalState.project!.project_images!.length - 1 : prev - 1
        );
      }
      setImageLoading(false);
    }, 150);
  };
  const retryLoad = () => {
    if (retryCount < 2) {
      setRetryCount(prev => prev + 1);
      loadProjects();
    }
  };

  // Utilitaires
  const getAnimationStyle = (index: number): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.6s ease-out ${index * 0.1}s, transform 0.6s ease-out ${index * 0.1}s`
  });

  // Fonctions pour gérer les traductions des projets
  const getProjectTitle = (project: ExtendedProject): string => {
    if (language === 'en' && project.title_en) {
      return project.title_en || 'Project';
    }
    return project.title || (language === 'en' ? 'Project' : 'Projet');
  };

  const getProjectDescription = (project: ExtendedProject): string => {
    if (language === 'en' && project.description_en) {
      return project.description_en || 'Description coming soon';
    }
    return project.description || (language === 'en' ? 'Description coming soon' : 'Description à venir');
  };

  const getProjectBadgeText = (project: ExtendedProject): string => {
    if (language === 'en' && project.badge_en) {
      return project.badge_en;
    }
    return project.badge || '';
  };

  const getMainImage = (project: ExtendedProject): string => {
    if (project.project_images && project.project_images.length > 0) {
      const mainImage = project.project_images.find(img => img.is_primary);
      if (mainImage?.image_url) return normalizeImageUrl(mainImage.image_url);
      const firstImage = project.project_images[0];
      if (firstImage?.image_url) return normalizeImageUrl(firstImage.image_url);
    }
    if (project.image_url) return normalizeImageUrl(project.image_url);
    return '/images/placeholder-project.svg';
  };
  const getProjectTechnologies = (project: ExtendedProject): string[] => {
    if (!project.technologies) return [];
    if (Array.isArray(project.technologies)) return project.technologies;
    if (typeof project.technologies === 'string') {
      try {
        const parsed = JSON.parse(project.technologies);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return (project.technologies as string)
          .split(',')
          .map((t: string) => t.trim())
          .filter(Boolean);
      }
    }
    return [];
  };
  const getProjectBadge = (project: ExtendedProject): { text: string; icon: React.ReactNode } => {
    const badgeText = getProjectBadgeText(project);

    if (badgeText) {
      if (badgeText.toLowerCase().includes('featured')) {
        return { text: badgeText, icon: <FaStar /> };
      }
      if (badgeText.toLowerCase().includes('academic') || badgeText.toLowerCase().includes('examen')) {
        return { text: badgeText, icon: <FaGraduationCap /> };
      }
      if (badgeText.toLowerCase().includes('openclassrooms') || badgeText.toLowerCase().includes('cursus')) {
        return { text: badgeText, icon: <FaGraduationCap /> };
      }
      return { text: badgeText, icon: <FaBriefcase /> };
    }
    
    if (project.is_academic) {
      // Détection automatique si le projet provient du cursus OpenClassrooms
      const isOpenClassrooms =
        Boolean(project.badge_image_url?.toLowerCase().includes('openclassrooms')) ||
        Boolean(project.description?.toLowerCase().includes('openclassrooms')) ||
        project.title.toLowerCase().startsWith('projet');

      if (isOpenClassrooms) {
        return {
          text: language === 'en' ? 'OpenClassrooms · Data Engineering & AI' : 'OpenClassrooms · Data Engineering & IA',
          icon: <FaGraduationCap />
        };
      }

      return { 
        text: language === 'en' ? '🎓 Academic' : '🎓 Académique', 
        icon: <FaGraduationCap /> 
      };
    }
    
    return { 
      text: language === 'en' ? '💼 Professional' : '💼 Professionnel', 
      icon: <FaBriefcase /> 
    };
  };
  // URL du logo de badge (carré) affiché en overlay sur l'image, configurable dans l'admin
  const getBadgeLogoUrl = (project: ExtendedProject): string | null => {
    const raw = (project as any).badge_image_url as string | null | undefined;
    if (!raw) return null;
    return normalizeImageUrl(raw);
  };

  // Overlay carré du logo (entreprise / école) en bas à droite de l'image du projet
  const renderBadgeLogoOverlay = (project: ExtendedProject): React.ReactNode => {
    const url = getBadgeLogoUrl(project);
    if (!url) return null;
    return (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: '8px',
          right: '8px',
          width: '42px',
          height: '42px',
          objectFit: 'contain',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
          zIndex: 3,
        }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  };

  const truncateDescription = (description: string, maxLength: number = 150): string => {
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  };
  const isProjectFeatured = (project: ExtendedProject): boolean => {
    return project.is_featured || project.badge?.toLowerCase().includes('featured') || false;
  };

  // Catégorie d'un projet pour le filtrage rapide (Académique / Production / Freelance).
  // Logique partagée avec le dashboard admin (source de vérité unique).
  const getProjectCategory = (project: ExtendedProject): ProjectCategory =>
    getSharedProjectCategory(project);

  const CATEGORY_FILTERS: { key: string; label: string }[] = [
    { key: 'academique', label: 'Académique' },
    { key: 'production', label: 'Production' },
    { key: 'freelance', label: 'Freelance' },
    { key: 'alternance', label: 'Alternance/Stage' },
  ];

  // Calculs pour le rendu
  const safeProjects = Array.isArray(projects) ? projects : [];
  const filteredProjects = filterState.active
    ? safeProjects.filter(project => {
        const technologies = getProjectTechnologies(project);
        return technologies.some(tech =>
          tech.toLowerCase().includes(filterState.active!.toLowerCase())
        );
      })
    : safeProjects;
  const visibleProjects = filteredProjects.filter(project => project.is_visible);
  const categoryFilteredProjects = categoryFilter
    ? visibleProjects.filter(project => getProjectCategory(project) === categoryFilter)
    : visibleProjects;

  // Séparation des projets
  const siteVitrineProjects = categoryFilteredProjects.filter(p => !p.is_academic);
  const academicProjects = categoryFilteredProjects.filter(p => p.is_academic);
  const activeFilterCount = filterState.active ? 1 : 0;

  // Rendu conditionnel - chargement
  if (loading) {
    return (
      <section id="projects" className={`${commonClasses.section} ${projectsStyles.section}`}>
        <h2 className={commonClasses.title}>
          <span className={commonClasses.highlight} aria-hidden="true">04.</span>
          {t('projects.title') || 'Mes Projets'}
        </h2>
        <div className={projectsStyles.loading}>
          <Spinner size="large" ariaLabel={t('projects.loading') || 'Chargement des projets'} />
        </div>
      </section>
    );
  }

  // Rendu conditionnel - erreur
  if (error && visibleProjects.length === 0) {
    return (
      <section id="projects" className={`${commonClasses.section} ${projectsStyles.section}`}>
        <h2 className={commonClasses.title}>
          <span className={commonClasses.highlight} aria-hidden="true">04.</span>
          {t('projects.title') || 'Mes Projets'}
        </h2>
        <div className={projectsStyles.error}>
          <h3>❌ Erreur de chargement</h3>
          <p>{error}</p>
          <p className={projectsStyles.errorDetails}>
            Tentatives : {retryCount}/2
          </p>
          <button
            onClick={retryLoad}
            className={projectsStyles.retryButton}
            disabled={retryCount >= 2}
          >
            {retryCount >= 2 ? 'Limite atteinte' : (t('common.retry') || 'Réessayer')}
          </button>
        </div>
      </section>
    );
  }

  // Rendu principal
  return (
    <section
      id="projects"
      ref={ref}
      className={`${commonClasses.section} ${projectsStyles.section}`}
      aria-labelledby="projects-heading"
    >
      <div className={projectsStyles.header}>
        <h2
          id="projects-heading"
          className={`${commonClasses.title} ${projectsStyles.title}`}
        >
          <span className={commonClasses.highlight} aria-hidden="true">04.</span>
          <span data-translate="true">
            {t('projects.title') || 'Mes Projets'}
          </span>
        </h2>
        <div className={projectsStyles.headerActions}>
          {CATEGORY_FILTERS.map(cat => (
            <button
              key={cat.key}
              type="button"
              className={`${projectsStyles.filterButton} ${categoryFilter === cat.key ? projectsStyles.active : ''}`}
              onClick={() => setCategoryFilter(prev => (prev === cat.key ? null : cat.key))}
              aria-pressed={categoryFilter === cat.key}
            >
              {cat.label}
            </button>
          ))}
          <button
            className={projectsStyles.filterToggle}
            onClick={toggleFiltersVisibility}
            aria-label="Afficher les filtres"
            aria-expanded={filterState.visible}
          >
            <FaFilter />
            <span>Filtres</span>
            {activeFilterCount > 0 && (
              <span className={projectsStyles.filterBadge}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filtres */}
      {filterState.visible && (
        <div className={projectsStyles.filtersContainer}>
          <div className={projectsStyles.filters}>
            <button
              className={`${projectsStyles.filterButton} ${!filterState.active ? projectsStyles.active : ''}`}
              onClick={() => handleFilter(null)}
            >
              Tous
            </button>
            {TECH_FILTERS.map(tech => (
              <button
                key={tech}
                className={`${projectsStyles.filterButton} ${filterState.active === tech ? projectsStyles.active : ''}`}
                onClick={() => handleFilter(tech)}
              >
                {tech}
              </button>
            ))}
          </div>
          {activeFilterCount > 0 && (
            <button
              className={projectsStyles.clearFilters}
              onClick={clearFilters}
            >
              <FaRegTimesCircle /> Effacer les filtres
            </button>
          )}
        </div>
      )}

      {/* Liste des projets sous forme de cards */}
      <div className={projectsStyles.projectsList}>
        {/* Projets vitrine */}
        {siteVitrineProjects.length === 0 ? (
          (siteVitrineProjects.length + academicProjects.length === 0) ? (
            <div className={projectsStyles.noProjects}>
              <h3>📂 Aucun projet disponible</h3>
              <p>Les projets seront bientôt disponibles.</p>
            </div>
          ) : null
        ) : (
          siteVitrineProjects.map((project, index) => (
            <div
              key={project.id}
              className={`${projectsStyles.project} ${isProjectFeatured(project) ? projectsStyles.featuredProject : ''}`}
              style={getAnimationStyle(index)}
            >
              <div className={projectsStyles.projectBadge}>
                {getProjectBadge(project).icon} {getProjectBadge(project).text}
              </div>
              <div
                className={projectsStyles.projectImageWrapper}
                onClick={() => openModal(project)}
                role="button"
                tabIndex={0}
                aria-label={`Voir les détails de ${getProjectTitle(project)}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal(project);
                  }
                }}
              >
                <img
                  src={getMainImage(project)}
                  alt={getProjectTitle(project)}
                  className={projectsStyles.projectImage}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.src = '/images/placeholder-project.svg';
                  }}
                />
                <div className={projectsStyles.imageOverlay}>
                  <span className={projectsStyles.viewImages}>
                    <FaEye /> Voir les détails
                  </span>
                </div>
                {renderBadgeLogoOverlay(project)}
              </div>
              <div className={projectsStyles.projectContent}>
                <h3 className={projectsStyles.projectTitle}>
                  {getProjectTitle(project)}
                </h3>
                <p className={projectsStyles.projectDescription}>
                  {truncateDescription(getProjectDescription(project))}
                </p>
                <div className={projectsStyles.tags}>
                  {getProjectTechnologies(project).slice(0, 4).map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className={projectsStyles.tag}
                      title={`Filtrer par ${tech}`}
                      onClick={() => handleFilter(tech)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFilter(tech);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <div className={projectsStyles.projectLinks}>
                  {project.live_url && project.show_live_url && (
                    <a
                      href={project.live_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={projectsStyles.projectLink}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Voir le site de ${getProjectTitle(project)}`}
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                  {project.github_url && project.show_github_url && (
                    <a
                      href={project.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={projectsStyles.projectLink}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Voir le code de ${getProjectTitle(project)}`}
                    >
                      <FaGithub />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Projets d'examen (affichés seulement si showAcademic) */}
        {(showAcademic || categoryFilter === 'academique') && academicProjects.map((project, index) => (
          <div
            key={project.id}
            className={`${projectsStyles.project} ${isProjectFeatured(project) ? projectsStyles.featuredProject : ''}`}
            style={getAnimationStyle(index + siteVitrineProjects.length)}
          >
            <div className={projectsStyles.projectBadge}>
              {getProjectBadge(project).icon} {getProjectBadge(project).text}
            </div>
            <div
              className={projectsStyles.projectImageWrapper}
              onClick={() => openModal(project)}
              role="button"
              tabIndex={0}
              aria-label={`Voir les détails de ${getProjectTitle(project)}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openModal(project);
                }
              }}
            >
              <img
                src={getMainImage(project)}
                alt={getProjectTitle(project)}
                className={projectsStyles.projectImage}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = '/images/placeholder-project.svg';
                }}
              />
              <div className={projectsStyles.imageOverlay}>
                <span className={projectsStyles.viewImages}>
                  <FaEye /> Voir les détails
                </span>
              </div>
              {renderBadgeLogoOverlay(project)}
            </div>
            <div className={projectsStyles.projectContent}>
              <h3 className={projectsStyles.projectTitle}>
                {getProjectTitle(project)}
              </h3>
              <p className={projectsStyles.projectDescription}>
                {truncateDescription(getProjectDescription(project))}
              </p>
              <div className={projectsStyles.tags}>
                {getProjectTechnologies(project).slice(0, 4).map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className={projectsStyles.tag}
                    title={`Filtrer par ${tech}`}
                    onClick={() => handleFilter(tech)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFilter(tech);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <div className={projectsStyles.projectLinks}>
                {project.live_url && project.show_live_url && (
                  <a
                    href={project.live_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={projectsStyles.projectLink}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Voir le site de ${getProjectTitle(project)}`}
                  >
                    <FaExternalLinkAlt />
                  </a>
                )}
                {project.github_url && project.show_github_url && (
                  <a
                    href={project.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={projectsStyles.projectLink}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Voir le code de ${getProjectTitle(project)}`}
                  >
                    <FaGithub />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Bouton toujours visible sous la liste */}
        {academicProjects.length > 0 && categoryFilter !== 'academique' && (
          <div style={{ textAlign: 'center', margin: '2rem 0' }}>
            <button
              className={projectsStyles.actionButton}
              onClick={() => setShowAcademic(v => !v)}
            >
              {showAcademic 
                ? (language === 'en' ? 'Hide academic projects' : 'Masquer les projets d\'examen')
                : (language === 'en' ? 'View academic projects' : 'Voir les projets d\'examen')
              }
            </button>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {modalState.isOpen && modalState.project && (
        <div className={projectsStyles.modal} onClick={closeModal}>
          <div className={projectsStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={projectsStyles.modalClose}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
              }}
              aria-label="Fermer la modal"
            >
              <FaTimes />
            </button>
            <div className={projectsStyles.modalHeader}>
              <h2>{getProjectTitle(modalState.project)}</h2>
              <div className={projectsStyles.modalBadge}>
                {getProjectBadge(modalState.project).icon} {getProjectBadge(modalState.project).text}
              </div>
            </div>
            <div className={projectsStyles.modalBody}>
              {/* Afficher toujours une image - soit la galerie, soit l'image principale */}
              <div className={projectsStyles.imageGallery}>
                <div className={projectsStyles.mainImageContainer}>
                  <img
                    src={
                      modalState.project.project_images && modalState.project.project_images.length > 0
                        ? normalizeImageUrl(modalState.project.project_images[currentImageIndex]?.image_url) || getMainImage(modalState.project)
                        : getMainImage(modalState.project)
                    }
                    alt={
                      modalState.project.project_images && modalState.project.project_images.length > 0
                        ? modalState.project.project_images[currentImageIndex]?.alt_text || getProjectTitle(modalState.project)
                        : getProjectTitle(modalState.project)
                    }
                    className={`${projectsStyles.mainImage} ${imageLoading ? projectsStyles.loading : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (!modalState.project) return;
                      const imageUrl = modalState.project.project_images && modalState.project.project_images.length > 0
                        ? normalizeImageUrl(modalState.project.project_images[currentImageIndex]?.image_url) || getMainImage(modalState.project)
                        : getMainImage(modalState.project);
                      window.open(imageUrl, '_blank', 'noopener,noreferrer');
                    }}
                    title="Cliquer pour ouvrir l'image en grand"
                  />
                  {modalState.project.project_images && modalState.project.project_images.length > 1 && (
                    <>
                      <button
                        className={`${projectsStyles.navButton} ${projectsStyles.prevButton}`}
                        onClick={() => navigateImage('prev')}
                        aria-label="Image précédente"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        className={`${projectsStyles.navButton} ${projectsStyles.nextButton}`}
                        onClick={() => navigateImage('next')}
                        aria-label="Image suivante"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                </div>
                {modalState.project.project_images && modalState.project.project_images.length > 1 && (
                  <div className={projectsStyles.thumbnails}>
                    {modalState.project.project_images.map((image, index) => (
                      <button
                        key={image.id}
                        className={`${projectsStyles.thumbnail} ${index === currentImageIndex ? projectsStyles.active : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Voir l'image ${index + 1}`}
                      >
                        <img
                          src={normalizeImageUrl(image.image_url)}
                          alt={image.alt_text || `Image ${index + 1}`}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={projectsStyles.projectDetails}>
                <p className={projectsStyles.fullDescription}>
                  {getProjectDescription(modalState.project)}
                </p>
                <div className={projectsStyles.allTechnologies}>
                  <h4>Technologies utilisées</h4>
                  <div className={projectsStyles.techGrid}>
                    {getProjectTechnologies(modalState.project).map((tech, index) => (
                      <span key={index} className={projectsStyles.techTag}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                {modalState.project.project_features && modalState.project.project_features.length > 0 && (
                  <div className={projectsStyles.features}>
                    <h4>Fonctionnalités principales</h4>
                    <ul>
                      {modalState.project.project_features
                        .sort((a, b) => a.order_index - b.order_index)
                        .map((feature) => (
                          <li key={feature.id}>
                            <strong>{feature.name}</strong>: {feature.description}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
                <div className={projectsStyles.modalActions}>
                  {modalState.project.live_url && modalState.project.show_live_url && (
                    <a
                      href={modalState.project.live_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${projectsStyles.actionButton} ${projectsStyles.liveButton}`}
                    >
                      <FaPlay /> Voir le site
                    </a>
                  )}
                  {modalState.project.github_url && modalState.project.show_github_url && (
                    <a
                      href={modalState.project.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${projectsStyles.actionButton} ${projectsStyles.codeButton}`}
                    >
                      <FaCode /> Voir le code
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
